/** @jsxImportSource @emotion/react */
import type { ApiScanVideoSubmission } from '@kaiizen/kai-pro-api';
import { useUsers } from '@kaiizen/kai-pro-api-react-state';
import { useMemo } from 'react';

import type { VideoToolboxClipReadStatus } from '../../VideoToolbox/videoToolboxClipReadStatusLib';
import type { FocusedMediaStripVideoEntry } from '../focusedMediaStripLib';
import { FocusedStripHeader } from './FocusedStripHeader';
import { FocusedStripPhotoTile } from './FocusedStripPhotoTile';
import { getDragSourcesForPhoto } from './focusedStripPhotoTileLib';
import { DEFAULT_FOCUSED_STRIP_STYLES, type FocusedStripStyles } from './focusedStripStyles';
import {
  FOCUSED_STRIP_MAX_TILES,
  type FocusedPhotoStripEntry,
  type FocusedStripCountMode,
  MORE_PHOTOS_TILE_LABEL,
} from './focusedStripTypes';
import { FocusedStripVideoTile } from './FocusedStripVideoTile';
import { useFocusedStripPhotoSelection } from './useFocusedStripPhotoSelection';

export type FocusedStripProps = {
  photos: readonly FocusedPhotoStripEntry[];
  videos?: readonly FocusedMediaStripVideoEntry[];
  submissionsByVideoId?: Record<string, ApiScanVideoSubmission>;
  emptyLabel: string;
  title?: string;
  actionLabel?: string;
  showTaskChipLabels?: boolean;
  countMode?: FocusedStripCountMode;
  styles?: FocusedStripStyles;
  testId: 'focused-photo-strip' | 'focused-media-strip';
  onAction?: () => void;
  onOpenPhotoManager?: () => void;
  onSelectPhoto: (photo: FocusedPhotoStripEntry) => void;
  onSelectVideo?: (video: FocusedMediaStripVideoEntry, submission?: ApiScanVideoSubmission) => void;
  resolveVideoClipReadStatus?: (video: FocusedMediaStripVideoEntry) => VideoToolboxClipReadStatus;
};

export const FocusedStrip = ({
  photos,
  videos = [],
  submissionsByVideoId = {},
  emptyLabel,
  title,
  actionLabel,
  showTaskChipLabels = true,
  countMode = videos.length ? 'media' : 'photos-only',
  styles = DEFAULT_FOCUSED_STRIP_STYLES,
  testId,
  onAction,
  onOpenPhotoManager,
  onSelectPhoto,
  onSelectVideo,
  resolveVideoClipReadStatus,
}: FocusedStripProps) => {
  const { usersById } = useUsers();
  const totalMediaCount = photos.length + videos.length;
  const maxTiles = FOCUSED_STRIP_MAX_TILES;
  const visibleVideoCount = Math.min(videos.length, maxTiles);
  const visibleVideos = videos.slice(0, visibleVideoCount);
  const visiblePhotos = photos.slice(0, maxTiles - visibleVideoCount);
  const overflowCount = totalMediaCount - visibleVideos.length - visiblePhotos.length;

  const countParts = useMemo(() => {
    const parts: string[] = [];
    if (photos.length) parts.push(`${photos.length} ${photos.length === 1 ? 'photo' : 'photos'}`);
    if (videos.length) parts.push(`${videos.length} ${videos.length === 1 ? 'video' : 'videos'}`);
    return parts;
  }, [photos.length, videos.length]);

  const { selectedPhotoIds, handlePhotoTileClick } = useFocusedStripPhotoSelection(visiblePhotos);

  return (
    <div data-testid={testId}>
      <FocusedStripHeader
        title={title}
        countMode={countMode}
        photoCount={photos.length}
        countParts={countParts}
        actionLabel={actionLabel}
        styles={styles}
        onAction={onAction}
        onOpenPhotoManager={onOpenPhotoManager}
      />
      {totalMediaCount
        ? (
          <div css={styles.scroller}>
            {visibleVideos.map(video => (
              <FocusedStripVideoTile
                key={video.videoId}
                video={video}
                submission={submissionsByVideoId[video.videoId]}
                readStatus={resolveVideoClipReadStatus?.(video) ?? 'NOT READ'}
                showTaskChipLabels={showTaskChipLabels}
                styles={styles}
                onClick={() => onSelectVideo?.(video, submissionsByVideoId[video.videoId])}
              />
            ))}
            {visiblePhotos.map((photo, index) => (
              <FocusedStripPhotoTile
                key={photo.photoId}
                photo={photo}
                authorName={photo.addedByUserId ? usersById?.[photo.addedByUserId]?.name : undefined}
                showTaskChipLabels={showTaskChipLabels}
                selected={selectedPhotoIds.has(photo.photoId)}
                dragSources={getDragSourcesForPhoto(photo, visiblePhotos, selectedPhotoIds)}
                styles={styles}
                onClick={event => handlePhotoTileClick(photo, index, event, onSelectPhoto)}
              />
            ))}
            {overflowCount > 0 && onOpenPhotoManager
              ? (
                <button
                  type={'button'}
                  css={styles.overflowTile}
                  onClick={onOpenPhotoManager}
                  title={`${overflowCount} more — open the media manager`}
                  data-testid={testId === 'focused-media-strip' ? 'focused-media-more-tile' : 'focused-photo-more-tile'}
                >
                  {MORE_PHOTOS_TILE_LABEL}
                </button>
              )
              : null}
          </div>
        )
        : <div css={styles.empty}>{emptyLabel}</div>}
    </div>
  );
};
